import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { FaServer, FaSyncAlt, FaSave, FaPlug, FaUndo } from "react-icons/fa";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import SidebarPanelNavigation from "../../../../components/SidebarPanelNavigation";
import useAuth from "../../../../contexts/auth";
import {
  getWhatsAppWebService,
  updateWhatsAppWebService,
  testWhatsAppWebService,
  getWhatsAppWebServiceHealth,
  getWhatsAppWebServiceRoutes,
  getWhatsAppWebServiceOverview,
} from "../../../../services/api";

ChartJS.register(ArcElement, Tooltip, Legend);

function formatUptime(seconds) {
  if (seconds == null) return "-";
  const s = Number(seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatBytes(bytes) {
  if (bytes == null) return "-";
  const mb = Number(bytes) / 1048576;
  return `${mb.toFixed(1)} MB`;
}

function formatDateTime(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch (e) {
    return value;
  }
}

function MetricCard({ label, value, hint, variant }) {
  return (
    <Card className="mb-3 h-100">
      <Card.Body className="py-3">
        <div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>
          {label}
        </div>
        <div
          style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}
          className={variant ? `text-${variant}` : ""}
        >
          {value ?? "-"}
        </div>
        {hint && <small className="text-muted">{hint}</small>}
      </Card.Body>
    </Card>
  );
}

const SOURCE_LABEL = { painel: "Painel", env: ".env" };

export default function ServicePage() {
  const { user } = useAuth();

  const [config, setConfig] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [overview, setOverview] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const [form, setForm] = useState({ base_url: "", timeout: "", token: "" });

  const loadConfig = useCallback(async () => {
    const res = await getWhatsAppWebService();
    const data = res?.data;
    setConfig(data);
    setForm({
      base_url: data?.base_url ?? "",
      timeout: data?.timeout ?? "",
      token: "",
    });
    return data;
  }, []);

  const loadHealth = useCallback(async () => {
    setChecking(true);
    try {
      const res = await getWhatsAppWebServiceHealth();
      setHealth(res?.data ?? null);
    } catch (e) {
      setHealth({ online: false, error: "service_unreachable" });
    } finally {
      setChecking(false);
    }
  }, []);

  const loadRoutes = useCallback(async () => {
    const res = await getWhatsAppWebServiceRoutes();
    setRoutes(res?.data?.routes ?? []);
  }, []);

  const loadOverview = useCallback(async () => {
    const res = await getWhatsAppWebServiceOverview(30);
    setOverview(res?.data ?? null);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadConfig(), loadRoutes()]);
        await Promise.all([loadHealth(), loadOverview()]);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadConfig, loadRoutes, loadHealth, loadOverview]);

  useEffect(() => {
    const i = setInterval(() => {
      loadHealth();
      loadOverview();
    }, 15000);
    return () => clearInterval(i);
  }, [loadHealth, loadOverview]);

  if (!user) return <Redirect to="/entrar" />;
  if (user.type !== "admin") return <Redirect to="/" />;

  async function handleTest() {
    setTesting(true);
    setFeedback(null);
    try {
      const payload = {
        base_url: form.base_url || undefined,
        timeout: form.timeout || undefined,
        token: form.token || undefined,
      };
      const res = await testWhatsAppWebService(payload);
      const data = res?.data;
      if (data?.online) {
        setFeedback({
          variant: "success",
          text: `Conexão OK (${data.latency_ms}ms) em ${data.base_url}`,
        });
      } else {
        setFeedback({
          variant: "danger",
          text: `Sem resposta em ${data?.base_url || form.base_url}. Confira a URL/token.`,
        });
      }
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setFeedback(null);
    try {
      await updateWhatsAppWebService({
        base_url: form.base_url,
        timeout: form.timeout || null,
        token: form.token || undefined,
      });
      await loadConfig();
      await loadHealth();
      setFeedback({ variant: "success", text: "Configuração salva no painel." });
    } catch (e) {
      setFeedback({ variant: "danger", text: "Falha ao salvar (confira a URL)." });
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!window.confirm("Voltar a usar a URL/token do .env?")) return;
    setSaving(true);
    setFeedback(null);
    try {
      await updateWhatsAppWebService({ reset: true });
      await loadConfig();
      await loadHealth();
      setFeedback({ variant: "info", text: "Configuração voltou para o .env." });
    } finally {
      setSaving(false);
    }
  }

  const online = !!health?.online;

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3} lg={2}>
          <SidebarPanelNavigation isAdmin />
        </Col>
        <Col md={9} lg={10}>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h2 className="mb-1 d-flex align-items-center">
                <FaServer className="mr-2" style={{ color: "#25D366" }} /> Serviço WhatsApp
              </h2>
              <p className="text-muted mb-0">
                Saúde do microserviço e conexão (URL/token) sem depender do .env
              </p>
            </div>
            <Button variant="outline-secondary" onClick={loadHealth} disabled={checking}>
              <FaSyncAlt /> {checking ? "Checando..." : "Checar agora"}
            </Button>
          </div>

          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              {feedback && (
                <Alert variant={feedback.variant} dismissible onClose={() => setFeedback(null)}>
                  {feedback.text}
                </Alert>
              )}

              <Card className="mb-4">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-1">Status do serviço</h5>
                      <small className="text-muted">
                        {health?.base_url || config?.base_url || "-"}
                      </small>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant={online ? "success" : "danger"}
                        style={{ padding: "8px 12px", fontSize: 13 }}
                      >
                        ● {online ? "Online" : "Offline"}
                      </Badge>
                      {health?.latency_ms != null && (
                        <div className="mt-2">
                          <small className="text-muted">{health.latency_ms}ms</small>
                        </div>
                      )}
                    </div>
                  </div>
                  {health?.service?.service && (
                    <div className="mt-3">
                      <small className="text-muted">
                        serviço: <strong>{health.service.service}</strong>
                        {health.service.timestamp ? ` · ${health.service.timestamp}` : ""}
                      </small>
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Visão geral do serviço</h5>
                    <small className="text-muted">
                      {overview?.monitoring?.checks_24h ?? 0} checagens em 24h
                    </small>
                  </div>

                  <Row>
                    <Col xs={6} md={4} lg={3}>
                      <MetricCard
                        label="Uptime 24h"
                        value={
                          overview?.monitoring?.uptime_24h != null
                            ? `${overview.monitoring.uptime_24h}%`
                            : "-"
                        }
                        hint={`7d: ${
                          overview?.monitoring?.uptime_7d != null
                            ? `${overview.monitoring.uptime_7d}%`
                            : "-"
                        }`}
                        variant={
                          (overview?.monitoring?.uptime_24h ?? 0) >= 99
                            ? "success"
                            : (overview?.monitoring?.uptime_24h ?? 0) >= 90
                            ? "warning"
                            : "danger"
                        }
                      />
                    </Col>
                    <Col xs={6} md={4} lg={3}>
                      <MetricCard
                        label="Latência"
                        value={`${overview?.service?.latency_ms ?? "-"}ms`}
                        hint={overview?.service?.base_url}
                      />
                    </Col>
                    <Col xs={6} md={4} lg={3}>
                      <MetricCard
                        label="Uptime do processo"
                        value={formatUptime(overview?.service?.uptime_seconds)}
                        hint={`desde ${formatDateTime(overview?.service?.started_at)}`}
                      />
                    </Col>
                    <Col xs={6} md={4} lg={3}>
                      <MetricCard
                        label="Sessões conectadas"
                        value={overview?.service?.sessions?.connected ?? 0}
                        hint={`de ${overview?.service?.sessions?.total ?? 0} sessões`}
                        variant={
                          (overview?.service?.sessions?.connected ?? 0) > 0
                            ? "success"
                            : "danger"
                        }
                      />
                    </Col>
                    <Col xs={6} md={4} lg={3}>
                      <MetricCard
                        label="Aguardando QR"
                        value={overview?.service?.sessions?.waiting_qr ?? 0}
                        hint="sessões sem conexão"
                      />
                    </Col>
                    <Col xs={6} md={4} lg={3}>
                      <MetricCard
                        label="Contatos em memória"
                        value={overview?.service?.sessions?.contacts ?? 0}
                        hint={`${overview?.service?.sessions?.recent_messages ?? 0} msgs no cache`}
                      />
                    </Col>
                    <Col xs={6} md={4} lg={3}>
                      <MetricCard
                        label="Reconexões"
                        value={overview?.service?.sessions?.reconnect_attempts ?? 0}
                        hint="tentativas acumuladas"
                      />
                    </Col>
                    <Col xs={6} md={4} lg={3}>
                      <MetricCard
                        label="Memória RSS"
                        value={formatBytes(overview?.service?.memory?.rss)}
                        hint={overview?.service?.node_version || "-"}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-2">
                    <Col md={6}>
                      <div style={{ height: 220 }}>
                        <Doughnut
                          data={{
                            labels: ["Conectadas", "Aguardando QR", "Outras"],
                            datasets: [
                              {
                                data: [
                                  overview?.service?.sessions?.connected ?? 0,
                                  overview?.service?.sessions?.waiting_qr ?? 0,
                                  Math.max(
                                    0,
                                    (overview?.service?.sessions?.total ?? 0) -
                                      (overview?.service?.sessions?.connected ?? 0) -
                                      (overview?.service?.sessions?.waiting_qr ?? 0)
                                  ),
                                ],
                                backgroundColor: ["#28a745", "#ffc107", "#6c757d"],
                                borderWidth: 0,
                              },
                            ],
                          }}
                          options={{
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { position: "bottom" },
                              title: { display: true, text: "Sessões do serviço" },
                            },
                          }}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div style={{ height: 220 }}>
                        <Doughnut
                          data={{
                            labels: ["Online", "Offline"],
                            datasets: [
                              {
                                data: [
                                  overview?.monitoring?.checks_online_24h ?? 0,
                                  overview?.monitoring?.checks_offline_24h ?? 0,
                                ],
                                backgroundColor: ["#28a745", "#dc3545"],
                                borderWidth: 0,
                              },
                            ],
                          }}
                          options={{
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { position: "bottom" },
                              title: { display: true, text: "Checagens (24h)" },
                            },
                          }}
                        />
                      </div>
                    </Col>
                  </Row>

                  <div className="mt-2">
                    <Badge
                      variant={
                        (overview?.monitoring?.consecutive_failures ?? 0) > 0
                          ? "danger"
                          : "success"
                      }
                    >
                      {(overview?.monitoring?.consecutive_failures ?? 0) > 0
                        ? `${overview.monitoring.consecutive_failures} falha(s) seguida(s)`
                        : "Sem falhas seguidas"}
                    </Badge>
                    <span className="ml-2 text-muted" style={{ fontSize: 13 }}>
                      Última checagem: {formatDateTime(overview?.monitoring?.last_check_at)} · Última queda:{" "}
                      {formatDateTime(overview?.monitoring?.last_offline_at)}
                    </span>
                  </div>
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Body>
                  <h5 className="mb-3">Conexão</h5>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        URL base do serviço{" "}
                        <Badge variant="light">
                          origem: {SOURCE_LABEL[config?.source?.base_url] || "-"}
                        </Badge>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="https://exemplo.ngrok-free.app"
                        value={form.base_url}
                        onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                      />
                      <Form.Text className="text-muted">
                        Troque aqui quando o ngrok mudar — não precisa mexer no .env.
                      </Form.Text>
                    </Form.Group>

                    <Row>
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Token interno{" "}
                            <Badge variant="light">
                              origem: {SOURCE_LABEL[config?.source?.token] || "-"}
                            </Badge>
                          </Form.Label>
                          <Form.Control
                            type="password"
                            placeholder={
                              config?.token_set
                                ? `definido (${config?.token_hint || "••••"}) — deixe vazio para manter`
                                : "não definido"
                            }
                            value={form.token}
                            onChange={(e) => setForm({ ...form, token: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Timeout (s)</Form.Label>
                          <Form.Control
                            type="number"
                            min={1}
                            max={120}
                            value={form.timeout}
                            onChange={(e) => setForm({ ...form, timeout: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex flex-wrap">
                      <Button
                        variant="primary"
                        className="mr-2 mb-2"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        <FaSave /> {saving ? "Salvando..." : "Salvar"}
                      </Button>
                      <Button
                        variant="outline-primary"
                        className="mr-2 mb-2"
                        onClick={handleTest}
                        disabled={testing}
                      >
                        <FaPlug /> {testing ? "Testando..." : "Testar conexão"}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        className="mb-2"
                        onClick={handleReset}
                        disabled={saving}
                      >
                        <FaUndo /> Restaurar do .env
                      </Button>
                    </div>
                  </Form>

                  <hr />
                  <small className="text-muted d-block">
                    Webhook (o serviço deve chamar):{" "}
                    <code>{config?.webhook_url}</code>
                  </small>
                </Card.Body>
              </Card>

              <Card>
                <Card.Body>
                  <h5 className="mb-3">Rotas do serviço</h5>
                  <Table responsive size="sm" hover>
                    <thead>
                      <tr>
                        <th>Grupo</th>
                        <th>Método</th>
                        <th>Rota</th>
                        <th>Descrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      {routes.map((r, i) => (
                        <tr key={i}>
                          <td>{r.group}</td>
                          <td>
                            <Badge variant="secondary">{r.method}</Badge>
                          </td>
                          <td>
                            <code>{r.path}</code>
                          </td>
                          <td className="text-muted">{r.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
}
